<template>
  <div class="component-wrapper inner d-flex flex-column align-center">
    <v-btn icon color="primary" @click="onClose" class="close-button">
      <v-icon>mdi-close</v-icon>
    </v-btn>

    <div class="body-2 text-center mb-2" v-if="role == 'superuser'">
      ({{ activeMuseum.defaultTitle }})
    </div>

    <div class="text-h6 primary--text text-center mb-1">{{ $t('step2') }}</div>

    <div class="text-h6 text-center title-underline mb-12">
      {{ $t('supportMaterial') }}
    </div>

    <div class="inner-wrapper">
      <Step1Info class="mb-8"/>

      <div class="d-flex align-center mb-8">
        <div class="subtitle-1 font-weight-medium">{{ $t('sections') }}</div>

        <v-btn
          small
          color="primary"
          class="ml-4"
          :disabled="showSection"
          :lang="$i18n.locale"
          @click="showSection = true"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t('newSection') }}
        </v-btn>
      </div>

      <div
        class="body-1"
        v-if="!currentProgram.sections.length && !showSection"
      >
        {{ $t('noAvailableSections') }}
      </div>

      <v-row
        v-for="section in currentProgram.sections"
        :key="section.id"
        class="d-flex align-center"
      >
        <v-col cols="3" class="body-1">
          {{ section.order }}. {{ section.defaultTitle }}
        </v-col>

        <v-col cols="9">
          <v-btn
            color="warning"
            small
            width="134px"
            class="mr-2"
            :disabled="showSection"
            :lang="$i18n.locale"
            @click="editSection(section)"
          >
            <v-icon left>mdi-pencil</v-icon>
            {{ $t('edit') }}
          </v-btn>

          <v-btn
            color="error"
            small
            width="134px"
            :disabled="showSection"
            :lang="$i18n.locale"
            @click="openDeleteDialog(section)"
          >
            <v-icon left>mdi-delete</v-icon>
            {{ $t('delete') }}
          </v-btn>
        </v-col>
      </v-row>
      
      <Section
        class="mt-10"
        v-if="showSection"
        :isEdit="!!selectedSection"
        :section="selectedSection"
        @closeSection="showSection = false"
      />

      <v-divider class="my-10"></v-divider>

      <div class="d-flex">
        <v-btn
          color="primary"
          small
          :disabled="showSection"
          :lang="$i18n.locale"
          @click="onPrev"
        >
          <v-icon left>mdi-chevron-left</v-icon>
          {{ $t('previous') }}
        </v-btn>

        <v-btn
          class="ml-auto"
          color="primary"
          small
          :disabled="!currentProgram.sections.length || showSection"
          :lang="$i18n.locale"
          @click="onNext"
        >
          {{ $t('next') }}
          <v-icon right>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="deleteDialog" max-width="500px">
      <div class="white pa-2" v-if="deleteDialog">
        <template v-if="currentProgram.sections != null && currentProgram.sections.length > 1">
          <div class="subtitle-1 text-center mb-2">
            {{ $t('areYouSureDeleteSection') }}
            {{ sectionToDelete.title }} ?
          </div>

          <div class="d-flex justify-center">
            <v-btn
              class="mr-2"
              small
              color="error"
              :loading="deleteLoader"
              :lang="$i18n.locale"
              @click="onDeleteSection"
            >
              {{ $t('delete') }}
            </v-btn>

            <v-btn
              small
              :lang="$i18n.locale"
              @click="closeDeleteDialog"
            >
              {{ $t('cancel') }}
            </v-btn>
          </div>
        </template>

        <template v-else>
          <div class="d-flex flex-column align-center">
            <v-icon class="primary rounded-circle pa-1 mb-2" color="white">
              mdi-exclamation
            </v-icon>

            <div class="subtitle-1 text-center mb-2">
              {{ $t('programMustHaveAtLeastOneSection') }}
            </div>

            <v-btn
              color="primary"
              small
              :lang="$i18n.locale"
              @click="deleteDialog = false"
            >
              {{ $t('okay') }}
            </v-btn>
          </div>
        </template>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import {mapActions, mapMutations, mapState} from "vuex";
import Step1Info from "@/views/program/step1/Step1Info";
import Section from "@/views/program/step2/Section";

export default {
  components: {
    Step1Info,
    Section,
  },
  data() {
    return {
      showSection: false,
      deleteDialog: false,
      deleteLoader: false,
      sectionToDelete: null,
      selectedSection: null,
    };
  },
  computed: {
    ...mapState({
      currentProgram: (state) => state.admin.currentProgram,
      role: (state) => state.auth.role,
      activeMuseum: (state) => state.superuser.activeMuseum,
    }),
    isEdit() {
      return this.$route.path.includes("edit");
    },
  },
  methods: {
    ...mapActions(["deleteSection"]),
    ...mapMutations(["setAllowStep3"]),
    editSection(section) {
      this.selectedSection = section;
      this.showSection = true;
    },
    openDeleteDialog(section) {
      this.deleteDialog = true;
      this.sectionToDelete = section;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.sectionToDelete = null;
    },
    async onDeleteSection() {
      this.deleteLoader = true;

      try {
        await this.deleteSection(this.sectionToDelete);
        this.deleteLoader = false;
        this.closeDeleteDialog();
      } catch (e) {
        console.log(e);
        this.deleteLoader = false;
        this.closeDeleteDialog();
      }
    },
    onClose() {
      if (this.role == "admin") {
        this.$router.push({path: "/"});
      } else {
        this.$router.push({
          path: `/superuser/museum/${this.activeMuseum.id}`,
        });
      }
    },
    onPrev() {
      let path;

      if (this.isEdit) {
        if (this.role == "admin") {
          path = `/program/edit/${this.currentProgram.uniqueCode}/step1`;
        } else {
          path = `/superuser/museum/${this.activeMuseum.id}/program/edit/${this.currentProgram.uniqueCode}/step1`;
        }
      } else {
        if (this.role == "admin") {
          path = `/program/new/step1`;
        } else {
          path = `/superuser/museum/${this.activeMuseum.id}/program/new/step1`;
        }
      }

      this.$router.push({
        path,
      });
    },
    onNext() {
      let path;

      if (this.isEdit) {
        if (this.role == "admin") {
          path = `/program/edit/${this.currentProgram.uniqueCode}/step3`;
        } else {
          path = `/superuser/museum/${this.activeMuseum.id}/program/edit/${this.currentProgram.uniqueCode}/step3`;
        }
      } else {
        this.setAllowStep3(true);
        if (this.role == "admin") {
          path = `/program/new/step3`;
        } else {
          path = `/superuser/museum/${this.activeMuseum.id}/program/new/step3`;
        }
      }

      this.$router.push({
        path,
      });
    },
  },
  watch: {
    showSection(newValue) {
      if (newValue) {
        this.$vuetify.goTo(999);
      } else {
        this.$vuetify.goTo(0);
        this.selectedSection = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
</style>